/**
 * Zod validation schemas for Organization Onboarding.
 */

import { z } from 'zod';

/**
 * Step 1: Organization Details
 * AC#2: name (required, 3-255 chars), slug (required, 3-100 chars, auto-generated)
 */
export const orgDetailsSchema = z.object({
  name: z
    .string()
    .trim()
    .min(3, 'Organization name must be at least 3 characters')
    .max(255, 'Organization name must be less than 255 characters'),
  slug: z
    .string()
    .trim()
    .min(3, 'Slug must be at least 3 characters')
    .max(100, 'Slug must be less than 100 characters')
    .regex(/^[a-z0-9-]+$/, 'Slug can only contain lowercase letters, numbers, and hyphens'),
});

export type OrgDetailsFormData = z.infer<typeof orgDetailsSchema>;

/**
 * Step 2: Optional Details
 * AC#4: description field (max 1000 chars)
 */
export const orgOptionalSchema = z.object({
  description: z
    .string()
    .max(1000, 'Description must be less than 1000 characters')
    .optional()
    .or(z.literal('')),
});

export type OrgOptionalFormData = z.infer<typeof orgOptionalSchema>;

/**
 * Invite member entry
 */
export const inviteMemberSchema = z.object({
  email: z.string().email('Invalid email address'),
  role: z.enum(['admin', 'member']),
});

export type InviteMemberData = z.infer<typeof inviteMemberSchema>;

/**
 * Step 3: Invite Members
 * AC#5: Invite up to 5 initial team members (email + role)
 */
export const inviteMembersSchema = z.object({
  invites: z
    .array(inviteMemberSchema)
    .max(5, 'You can invite up to 5 members')
    .refine(
      invites => {
        const emails = invites.map(i => i.email.toLowerCase());
        return new Set(emails).size === emails.length;
      },
      { message: 'Email addresses must be unique' },
    ),
});

export type InviteMembersFormData = z.infer<typeof inviteMembersSchema>;

/**
 * Combined schema for full onboarding form state.
 * Used by the wizard to manage all steps.
 */
export const onboardingFormSchema = orgDetailsSchema
  .merge(orgOptionalSchema)
  .merge(inviteMembersSchema);

export type OnboardingFormData = z.infer<typeof onboardingFormSchema>;

/**
 * Default values for the onboarding form.
 */
export const defaultOnboardingFormValues: OnboardingFormData = {
  name: '',
  slug: '',
  description: '',
  invites: [],
};
